import { memo } from 'react';

import { useComponentCategories } from '../../../utils/useComponentCategories';
import { List } from '../../../List/List';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpCategories } from '../../../../modules/categories';
import { usePlaceholders } from '../../usePlaceholders';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { CategoryItem } from '../../common/CategoryItem/CategoryItem';
import { useCategoryItemTracking } from '../../common/useCategoryItemTracking';

import type { SvpCategoryListComponent } from '@vgtv/api-client/lib/component/list';
import type { FC } from 'react';

import * as styles from './CategoriesList.module.scss';

type Props = {
  componentConfig: SvpCategoryListComponent;
  pageComponentIndex: number;
};

const CategoriesListBase: FC<Props> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isFetched } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpCategories,
  });

  const {
    id,
    header,
    type: componentType,
    options: { skipMetadata },
  } = componentConfig;

  const categories = useComponentCategories(id);

  const placeholders = usePlaceholders(isFetched);

  const { trackCategoryClick } = useCategoryItemTracking({
    elementType: 'CategoriesList',
    header,
    pageComponentIndex,
  });

  if (isFetched && categories.length === 0) {
    return null;
  }

  return (
    <List header={header} className={styles.list}>
      {[
        ...placeholders.map((item, index) => (
          <Placeholder type={placeholderTypes.category} key={index} />
        )),
        ...categories.map((category, index) => {
          return (
            <CategoryItem
              key={category.id}
              category={category}
              componentType={componentType}
              onClick={() => trackCategoryClick(category, index)}
              skipMetadata={skipMetadata}
            />
          );
        }),
      ]}
    </List>
  );
};

export const CategoriesList = memo(CategoriesListBase);
